/**
 * Phone Input Component
 * Formats input as phone number: (xxx) xxx-xxxx
 */

import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import { cn } from "@lib/utils/helpers";
import {
  formatPhoneNumber,
  cleanPhoneNumber,
} from "@/features/invitee/utils/inviteeFormatters";

/** Count numeric characters strictly before `caret` in `str`. */
function countDigitsBefore(str: string, caret: number): number {
  const end = Math.max(0, Math.min(caret ?? str.length, str.length));
  let n = 0;
  for (let i = 0; i < end; i++) {
    if (/\d/.test(str[i])) n++;
  }
  return n;
}

/**
 * Index in `formatted` to place the caret after `digitCount` digits have been seen
 * (cursor sits immediately after that digit).
 */
function caretIndexForDigitCount(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      seen++;
      if (seen === digitCount) {
        return i + 1;
      }
    }
  }
  return formatted.length;
}

interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "value"
  > {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** When false, only applies error border styling; caller renders the message (default true). */
  showErrorMessage?: boolean;
  /** Explicit test id for the error message. If omitted, uses `{data-testid}-error` when the input has `data-testid`. */
  errorDataTestId?: string;
}

export function PhoneInput({
  value,
  onChange,
  error,
  showErrorMessage = true,
  className,
  onBlur,
  errorDataTestId,
  ...props
}: PhoneInputProps) {
  const dataTestId =
    "data-testid" in props
      ? (props["data-testid"] as string | undefined)
      : undefined;
  const [displayValue, setDisplayValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Caret to apply after `displayValue` commits (set only from handleChange). */
  const pendingCaretRef = useRef<number | null>(null);

  useEffect(() => {
    if (value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync the formatted display to the value prop
      setDisplayValue(formatPhoneNumber(value));
    } else {
      setDisplayValue("");
    }
  }, [value]);

  useLayoutEffect(() => {
    const pos = pendingCaretRef.current;
    if (pos === null) return;
    const el = inputRef.current;
    pendingCaretRef.current = null;
    if (!el) return;
    const safe = Math.max(0, Math.min(pos, el.value.length));
    el.setSelectionRange(safe, safe);
  }, [displayValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const inputValue = input.value;
    const rawCaret =
      input.selectionStart ?? input.selectionEnd ?? inputValue.length;
    const digitCountBefore = countDigitsBefore(inputValue, rawCaret);

    const cleaned = cleanPhoneNumber(inputValue);
    const formatted = formatPhoneNumber(cleaned);
    const nextCaret = caretIndexForDigitCount(formatted, digitCountBefore);

    pendingCaretRef.current = nextCaret;
    setDisplayValue(formatted);
    onChange(cleaned);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (value) {
      setDisplayValue(formatPhoneNumber(value));
    }
    onBlur?.(e);
  };

  const resolvedErrorTestId =
    errorDataTestId ?? (dataTestId ? `${dataTestId}-error` : undefined);

  return (
    <>
      <input
        {...props}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="(xxx) xxx-xxxx"
        className={cn(
          "w-full placeholder:text-gray-500",
          error ? "!border-red-500 !focus:ring-red-500" : "",
          className,
        )}
      />
      {error && showErrorMessage && (
        <p className="mt-1 text-sm text-red-600" data-testid={resolvedErrorTestId}>
          {error}
        </p>
      )}
    </>
  );
}
